import { computed, onMounted, ref } from 'vue';
import md5 from 'md5';
import { useApiFieldErrors, useI18n, useMixpanel, createReportFormWorkspace, useWorkspace, useCurrencyList, t } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  getTrafficSourcesListApiRequest,
} from '@/services/api/traffic-sources-api';
import {
  putCampaignsBulkUpdateApiRequest,
} from '@/services/api/campaign-api';
import {
  populateTrafficSourcesStoreCacheApiRequest,
  updateTrafficSourcesStoreCacheApiRequest,
} from '@/services/api/api-services/traffic-sources';
import { populateOffersStoreCacheApiRequest } from '@/services/api/api-services/offers';
import { useCampaignFlowSelectOptions } from './components/useCampaignFlowSelectOptions';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import parseApiErrors, {
  parseFlowErrorsRaw,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';
import { useTableQuery } from '@/composables/useTableQuery';
import { useStore } from '@/store';

type BulkEditForm = {
  costModel: string;
  costValue: number;
  currencyId: string;
  maxCostEnabled: boolean;
  maxCostUsd: number | null;
  maxCostAction: 0 | 1;
  maxCostBotOfferId: string | null;
  trafficSource: TrafficSource | { id: string; name: string };
  trackingDomainId: string;
  trafficLossPercent: number;
  postbackPercent: number;
  uniquenessPeriod: number;
  tags: Array<{ id?: string; name: string } | string>;
  sendCustomConversionMode: string;
  sendCustomConversionIds: string[];
  doNotSendNotSpecifiedConversions: boolean;
  userAgentClientHintsEnabled: number;
  botFiltersMode: number;
  conversionNotificationUrl: string;
};

type CheckboxState = Record<string, boolean>;

type CustomEventItem = {
  sendPostback?: boolean;
  status?: string;
};

const defaultBulkForm = (): BulkEditForm => ({
  costModel: 'auto',
  costValue: 0,
  currencyId: 'USD',
  maxCostEnabled: false,
  maxCostUsd: null,
  maxCostAction: 1,
  maxCostBotOfferId: null,
  trafficSource: {
    name: t('None'),
    id: '',
  },
  trackingDomainId: '',
  trafficLossPercent: 0,
  postbackPercent: 100,
  uniquenessPeriod: 24 * 3600,
  tags: [],
  sendCustomConversionMode: 'all',
  sendCustomConversionIds: [],
  doNotSendNotSpecifiedConversions: false,
  userAgentClientHintsEnabled: 0,
  botFiltersMode: 0,
  conversionNotificationUrl: '',
});

const defaultCheckboxState = (): CheckboxState => ({
  trafficSource: false,
  tags: false,
  trackingDomainId: false,
  uniquenessPeriod: false,
  conversionNotificationUrl: false,
  currencyId: false,
  trafficLossPercent: false,
  postbackPercent: false,
  sendCustomConversion: false,
  userAgentClientHintsEnabled: false,
  botFiltersMode: false,
});

const dependentKeys: Record<string, string[]> = {
  currencyId: [
    'costValue',
    'costModel',
    'maxCostEnabled',
    'maxCostUsd',
    'maxCostAction',
    'maxCostBotOfferId',
  ],
};

export const useCampaignBulkEdit = (emitClose: () => void) => {
  const bulkEditReportWorkspace = createReportFormWorkspace();
  const { t, locale } = useI18n();
  const store = useStore();
  const workspaceApi = useWorkspace();
  const tableQueryApi = useTableQuery();
  const { trackMixpanel } = useMixpanel();
  const { currencyListOrderedPreferred } = useCurrencyList();

  const { fieldErrors, errorsControlActive, errorsDict } = useApiFieldErrors();

  const form = ref<BulkEditForm>(defaultBulkForm());
  const checkboxState = ref<CheckboxState>(defaultCheckboxState());
  const show = ref(false);
  const errorsRaw = ref<unknown[][] | null>(null);
  const errorMessage = ref('');
  const activeWorkspaceId = ref('');
  const trafficSourceSelectOptions = ref<TrafficSource[]>([]);
  const oldHash = ref('');

  const defaultWorkspace = bulkEditReportWorkspace.defaultWorkspace;
  const { fetchOfferOptions } = useCampaignFlowSelectOptions(() => ({
    workspaceId: activeWorkspaceId.value,
  }));
  const offerOptions = computed(() => store.cache.offers.data || []);

  const selectedItems = computed(() =>
    (tableQueryApi.tableSelectedItems.value as Array<{ isDirect?: boolean }>).filter(
      ({ isDirect }) => !isDirect,
    ),
  );

  const FFconversionNotificationUrlEnabled = computed(
    () => store.user.featureFlags?.conversionNotificationUrl?.enabled,
  );

  const postbackPercent = computed({
    get: () => form.value.postbackPercent,
    set: (value) => {
      form.value.postbackPercent = Number((+value || 0).toFixed(6).toString());
    },
  });

  const trafficLossPercent = computed({
    get: () => form.value.trafficLossPercent,
    set: (value) => {
      form.value.trafficLossPercent = Number((+value || 0).toFixed(6).toString());
    },
  });

  const uniquenessPeriod = computed({
    get: () => form.value.uniquenessPeriod / 3600,
    set: (value: number) => {
      form.value.uniquenessPeriod = value * 3600;
    },
  });

  const customConversions = computed(() => {
    const customEvents = Object.values(tableQueryApi.tableCustomEvents.value) as CustomEventItem[];
    return customEvents.filter(
      (item) => item.sendPostback && item.status === 'active',
    );
  });

  const trafficSourceSelectOptionsWithNone = computed(() => [
    {
      name: t('None'),
      id: '',
    },
    ...trafficSourceSelectOptions.value,
  ]);

  const allWorkspaceIds = computed(() => [
    ...new Set([activeWorkspaceId.value, ...workspaceApi.allPublicWorkspacesIds.value]),
  ]);

  const trackingDomains = computed(() =>
    store.trackingDomainsWithAccountDefault.filter((item) => {
      if (!item.id) return true;
      return allWorkspaceIds.value.some((workspace) => item.workspaceIds.includes(workspace));
    }),
  );

  const fetchTrafficSourcesOptions = computed(() => {
    if (store.cache.trafficSources.isAllInCache) {
      return;
    }

    return ({ search }: { search: string }) =>
      getTrafficSourcesListApiRequest({
        search,
        status: 'active',
        limit: '200',
        columns: ['name', 'id', 'workspaceId'],
        workspaceId: activeWorkspaceId.value,
      });
  });

  const errors = computed(() =>
    translateFlowErrorsRaw(errorsRaw.value, errorMessage.value),
  );

  const hasAtLeastOneConversion = computed(() => {
    const customEvents = Object.values(tableQueryApi.tableCustomEvents.value) as CustomEventItem[];
    return Boolean(customEvents.filter((item) => item.status === 'active').length);
  });

  const useClientHintsItems = computed(() => [
    {
      value: 0,
      text: t('Default (as in Global Settings)'),
    },
    {
      value: 1,
      text: t('Enabled (For This Campaign)'),
    },
    {
      value: 2,
      text: t('Disabled (For This Campaign)'),
    },
  ]);

  const botFiltersModeItems = computed(() => [
    {
      value: 0,
      text: t('Default (as in Global Settings)'),
    },
    {
      value: 1,
      text: t('Enabled (For This Campaign)'),
    },
    {
      value: 2,
      text: t('Disabled (For This Campaign)'),
    },
  ]);

  const activeElements = computed(() => {
    const data = Object.entries(checkboxState.value).reduce(
      (accumulator, [key, value]) => {
        if (value && key !== 'sendCustomConversion' && key !== 'trafficSource') {
          const dependentValues = dependentKeys[key]?.reduce(
            (dependentAccumulator, dependentKey) => {
              dependentAccumulator[dependentKey] = form.value[dependentKey as keyof BulkEditForm];
              return dependentAccumulator;
            },
            {} as Record<string, unknown>,
          );

          return {
            ...accumulator,
            [key]: form.value[key as keyof BulkEditForm],
            ...dependentValues,
          };
        }
        return accumulator;
      },
      {} as Record<string, unknown>,
    );

    if (checkboxState.value.sendCustomConversion) {
      data.sendCustomEventPostback = {
        mode: form.value.sendCustomConversionMode,
        ...(form.value.sendCustomConversionMode !== 'all'
          ? { ids: form.value.sendCustomConversionIds }
          : {}),
        doNotSendNotSpecifiedConversions: form.value.doNotSendNotSpecifiedConversions,
      };
    }

    if (checkboxState.value.trafficSource) {
      const trafficSource = form.value.trafficSource;
      data.trafficSourceId =
        typeof trafficSource === 'object' && trafficSource ? trafficSource.id : '';
    }

    if (checkboxState.value.currencyId) {
      if (!['auto', 'autoCpm'].includes(form.value.costModel)) {
        delete data.maxCostEnabled;
        delete data.maxCostUsd;
        delete data.maxCostAction;
        delete data.maxCostBotOfferId;
      } else if (!form.value.maxCostEnabled) {
        data.maxCostEnabled = false;
        delete data.maxCostUsd;
        delete data.maxCostAction;
        delete data.maxCostBotOfferId;
      } else {
        data.maxCostUsd = Number(form.value.maxCostUsd) || null;
        if (form.value.maxCostAction !== 0) {
          delete data.maxCostBotOfferId;
        } else {
          data.maxCostBotOfferId = form.value.maxCostBotOfferId || null;
        }
      }
    }

    return data;
  });

  const getCurrentHash = () =>
    md5(JSON.stringify({ ...form.value, ...checkboxState.value }));

  const setTrafficSourceOptions = async () => {
    if (!store.cache.trafficSources.isUploaded) {
      await populateTrafficSourcesStoreCacheApiRequest();
    }

    const { uploadedForWorkspace, isAllInCache, data, isUploaded } = store.cache.trafficSources;

    if (isUploaded && isAllInCache) {
      trafficSourceSelectOptions.value = data.filter(
        (item) => item.workspaceId === activeWorkspaceId.value,
      );
      return;
    }

    if (isUploaded && !isAllInCache && uploadedForWorkspace === activeWorkspaceId.value) {
      trafficSourceSelectOptions.value = data;
      return;
    }

    if (activeWorkspaceId.value === defaultWorkspace.value.id) {
      const result = await updateTrafficSourcesStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
      });
      trafficSourceSelectOptions.value = result as TrafficSource[];
      return;
    }

    trafficSourceSelectOptions.value = (await getTrafficSourcesListApiRequest({
      status: 'active',
      limit: '200',
      columns: ['name', 'id', 'workspaceId'],
      workspaceId: activeWorkspaceId.value,
    })) as TrafficSource[];
  };

  const load = async () => {
    form.value.currencyId = currencyListOrderedPreferred.value[0]?.code || 'USD';
  };

  const close = () => {
    emitClose();
    $emitter.emit('clear-report-selection');
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash()) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const onSendCustomConversionsClick = (value: string) => {
    if ((value === 'include' || value === 'exclude') && !hasAtLeastOneConversion.value) {
      $emitter.emit('open-dialog', {
        name: 'CustomConversionsSending',
      });
      return true;
    }
  };

  const trackAnalytics = (event: string, elementCount: number) => {
    const {
      postbackPercent: postbackPercentValue,
      trafficLossPercent: trafficLossPercentValue,
      uniquenessPeriod: uniquenessPeriodValue,
    } = form.value;

    const {
      trafficSource: trafficSourceEnabled,
      postbackPercent: postbackPercentEnabled,
      tags: tagsEnabled,
      trafficLossPercent: trafficLossPercentEnabled,
      trackingDomainId: trackingDomainIdEnabled,
      uniquenessPeriod: uniquenessPeriodEnabled,
      currencyId: currencyIdEnabled,
    } = checkboxState.value;

    trackMixpanel(event, {
      element_count: elementCount,
      ...(uniquenessPeriodEnabled && { uniqueness_period: uniquenessPeriodValue }),
      ...(postbackPercentEnabled && { postbackPercent: postbackPercentValue }),
      ...(trafficLossPercentEnabled && { trafficLossPercent: trafficLossPercentValue }),
      trafficSource_enabled: trafficSourceEnabled,
      tags_enabled: tagsEnabled,
      trackingDomain_enabled: trackingDomainIdEnabled,
      uniquenessPeriod_enabled: uniquenessPeriodEnabled,
      sendPostBacks_enabled: postbackPercentEnabled,
      trafficLoss_enabled: trafficLossPercentEnabled,
      currency_enabled: currencyIdEnabled,
    });
  };

  const submit = async () => {
    const ids = selectedItems.value.map(({ DT_RowId }) => DT_RowId);
    const params: {
      ids: string[];
      data: Record<string, unknown>;
    } = {
      ids,
      data: activeElements.value,
    };

    if (params.data?.tags) {
      params.data.tags = (params.data.tags as BulkEditForm['tags'])?.map((item) =>
        typeof item === 'object' && item?.id ? item.name : item,
      );
    }

    try {
      await putCampaignsBulkUpdateApiRequest(activeWorkspaceId.value, params);
      trackAnalytics('campaign_bulk_edit', ids.length);
      close();
      $emitter.emit('refresh-report');
      $emitter.emit('toast-success', t('Changes have been successfully saved'));
    } catch (error) {
      store.setLoading(false);
      const parsedFieldErrors = parseApiErrors({ error, nested: true });
      if (Object.keys(parsedFieldErrors).length === 0) {
        return;
      }

      fieldErrors.value = parsedFieldErrors;
      const responseData = (error as { response?: { data?: { errors?: unknown; message?: string } } })
        ?.response?.data;

      if (responseData?.errors) {
        errorsRaw.value = parseFlowErrorsRaw(responseData.errors);
        errorMessage.value = responseData.message || '';
        errorsControlActive.value = true;
      }
    }
  };

  onMounted(async () => {
    store.setLoading(true);

    activeWorkspaceId.value = selectedItems.value[0].DT_RowWorkspaceId;

    await load();

    store.setLoading(false);
    show.value = true;
    oldHash.value = getCurrentHash();

    await setTrafficSourceOptions();

    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }

    if (!store.cache.tags.campaigns.isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'campaigns' });
    }

    if (!store.cache.offers.isUploaded) {
      await populateOffersStoreCacheApiRequest();
    }
  });

  return {
    t,
    locale,
    store,

    show,
    form,
    checkboxState,
    selectedItems,
    trafficSourceSelectOptionsWithNone,
    fetchTrafficSourcesOptions,
    trackingDomains,
    FFconversionNotificationUrlEnabled,
    uniquenessPeriod,
    postbackPercent,
    trafficLossPercent,
    customConversions,
    useClientHintsItems,
    botFiltersModeItems,
    currencyListOrderedPreferred,
    offerOptions,
    fetchOfferOptions,
    errors,
    errorsDict,
    errorsControlActive,
    onSendCustomConversionsClick,
    checkClose,
    submit,
  };
};
